import type { CaptionSettings, VoiceoverSettings } from '@/types';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

function ok(): ValidationResult {
  return { valid: true, errors: [] };
}

function fail(...errors: string[]): ValidationResult {
  return { valid: false, errors };
}

/**
 * Validates voiceover placement settings. Rules:
 * - startAt must be >= 0.
 * - endAt (if set) must be greater than startAt.
 * - trimStart/trimEnd must be >= 0 and not exceed the generated duration combined.
 * - silenceBefore/silenceAfter must be >= 0.
 */
export function validateVoiceoverTiming(v: VoiceoverSettings): ValidationResult {
  const errors: string[] = [];

  if (v.startAt < 0) errors.push('Voiceover start time cannot be negative.');
  if (v.endAt !== null && v.endAt <= v.startAt) {
    errors.push('Voiceover end time must be after its start time.');
  }
  if (v.trimStart < 0 || v.trimEnd < 0) errors.push('Voiceover trim values cannot be negative.');
  if (v.silenceBefore < 0 || v.silenceAfter < 0) errors.push('Silence values cannot be negative.');

  if (v.generatedDurationSeconds !== null) {
    const remaining = v.generatedDurationSeconds - v.trimStart - v.trimEnd;
    if (remaining <= 0) {
      errors.push('Trim start + trim end exceeds the voiceover duration — nothing would remain.');
    }
  }

  return errors.length ? fail(...errors) : ok();
}

/** Validates caption settings for internally-consistent, renderable values. */
export function validateCaptionSettings(c: CaptionSettings): ValidationResult {
  const errors: string[] = [];

  if (c.maxCharsPerLine < 5) errors.push('Max characters per line must be at least 5.');
  if (c.maxLines < 1) errors.push('Captions must allow at least 1 line.');
  if (c.fontSizePx < 8 || c.fontSizePx > 300) errors.push('Font size must be between 8 and 300px.');
  if (c.widthPercent <= 0 || c.widthPercent > 100) errors.push('Caption width must be between 1% and 100%.');
  if (c.safeMarginPercent < 0 || c.safeMarginPercent > 40) errors.push('Safe margin must be between 0% and 40%.');
  if (c.outlineWidthPx < 0) errors.push('Outline thickness cannot be negative.');
  if (c.backgroundOpacity < 0 || c.backgroundOpacity > 1) errors.push('Background opacity must be between 0 and 1.');

  return errors.length ? fail(...errors) : ok();
}

/** Ensures uppercase/lowercase are mutually exclusive by construction (enum already enforces this,
 * this helper exists for UI code paths that build the enum value from two independent checkboxes). */
export function resolveCasing(uppercase: boolean, lowercase: boolean): CaptionSettings['casing'] {
  if (uppercase && !lowercase) return 'uppercase';
  if (lowercase && !uppercase) return 'lowercase';
  return 'original';
}
